/**
 * Маппинг start_param из Telegram на роуты приложения
 * Поддерживает регистронезависимое сравнение
 */

/**
 * Преобразует start_param в роут приложения
 * @param {string} startParam - параметр из Telegram WebApp
 * @returns {string|null} - путь роута или null если параметр невалидный
 */
export const getRouteFromStartParam = (startParam) => {
  if (!startParam || typeof startParam !== 'string') {
    return null
  }

  const param = startParam.toLowerCase()

  // Прямые ссылки на страницы
  // Например https://t.me/SkinozBot/app?startapp=Referrals
  const directRoutes = {
    'referrals': '/referrals',
    'cases': '/cases',
    'double': '/double',
    'mines': '/mines',
    'inventory': '/inventory',
    'profile': '/profile',
    'upgrader': '/upgrader',
    'contract': '/contract',
    'payment': '/payment',
    'transactions': '/transactions',
    'games': '/games',
    'event': '/event',
    'market': '/market',
    'playground': '/playground',
    'battles': '/battles',
    'royale': '/royale',
    'x50': '/x50',
    'crash': '/crash',
    'slot': '/slot',
    'slots': '/slot',
    'withdraw': '/withdraw',
    'rushdrop': '/rushdrop',
    // Банды: каталог и своя банда
    'teams': '/teams',
    'team': '/teams/my',
    'capture': '/teams/capture',
    // Экран недельного розыгрыша (кнопка из Telegram-пуша об announce)
    'rushdropweekly': '/rushdrop/weekly',
    // Алиас: startapp=flash → /inventory?tab=flash
    // (Telegram не передаёт ? в start_param, поэтому нужен отдельный ключ)
    'flash': '/inventory?tab=flash'
  }

  // Проверяем прямые роуты
  if (directRoutes[param]) {
    return directRoutes[param]
  }

  // Проверяем ссылку на конкретный кейс
  // Формат: case_ID или просто ID кейса
  if (param.startsWith('case_')) {
    const caseId = param.replace('case_', '')
    return `/cases/${caseId}`
  }

  // Проверяем ссылку на категорию кейсов
  // Формат: cat_slug
  if (param.startsWith('cat_')) {
    const categorySlug = param.replace('cat_', '')
    // Сохраняем категорию в localStorage для выбора после загрузки списка
    try {
      localStorage.setItem('selectedCaseCategory', categorySlug)
    } catch (e) {
      // ignore
    }
    return '/cases'
  }

  // Ссылка на публичный профиль пользователя
  // Формат: profile_<slug> (кнопка «Открыть профиль» из сообщения «Поделиться»)
  // Проверяется до generic-фолбака «длинная строка = ID кейса»
  if (param.startsWith('profile_')) {
    const profileSlug = startParam.slice(8)
    if (profileSlug) {
      return `/profile/${profileSlug}`
    }
  }

  // Ссылка на банду
  // Формат: team_<slug> (кнопка «Скопировать ссылку» в карточке банды)
  // Проверяется до generic-фолбака «длинная строка = ID кейса»
  if (param.startsWith('team_')) {
    const teamSlug = startParam.slice(5)
    if (teamSlug) {
      return `/teams/${teamSlug}`
    }
  }

  // Проверяем ссылку на промокод
  // Формат: promo_CODE — отрезаем ровно "promo_" (6 символов) от оригинального startParam,
  // т.к. код может содержать _ и -, а также быть регистрозависимым
  if (param.startsWith('promo_')) {
    const promoCode = startParam.slice(6)
    return `/promo/${promoCode}`
  }

  // Если параметр выглядит как ID кейса (длинная строка без пробелов)
  // Пробуем интерпретировать как прямую ссылку на кейс
  if (param.length > 20 && !param.includes(' ')) {
    return `/cases/${startParam}` // Используем оригинальный регистр для ID
  }

  return null
}

/**
 * Извлекает start_param из Telegram WebApp
 * @returns {string|null} - значение start_param или null
 */
export const getTelegramStartParam = () => {
  try {
    // Проверяем наличие Telegram WebApp API
    if (!window.Telegram?.WebApp) {
      return null
    }

    // Получаем start_param из initDataUnsafe
    const startParam = window.Telegram.WebApp.initDataUnsafe?.start_param

    if (startParam && typeof startParam === 'string') {
      return startParam
    }

    // Fallback: проверяем GET параметр tgWebAppStartParam
    const urlParams = new URLSearchParams(window.location.search)
    const urlStartParam = urlParams.get('tgWebAppStartParam')

    return urlStartParam || null
  } catch (error) {
    console.error('[StartParam] Error extracting start_param:', error)
    return null
  }
}
